import React, { useState, useEffect } from 'react';
import { flushSync } from 'react-dom';
import {
  FiMenu,
  FiHome,
  FiTable,
  FiBarChart2,
  FiShield,
  FiX,
  FiSettings,
  FiFileText,
  FiCpu,
  FiMap,
  FiLogOut,
  FiChevronRight,
} from 'react-icons/fi';
import { NavLink, useNavigate } from 'react-router-dom';
import { getAuthDisplayName, logout } from "../utils/cloudApi";
import { useLanguage } from '../Context/LanguageContext';

import { MdEco } from "react-icons/md";

const navItems = [
  { labelKey: 'sidebar.dashboard', icon: <FiHome />, path: '/dashboard' },
  { labelKey: 'sidebar.installationMap', icon: <FiMap />, path: '/installation-map' },
  { labelKey: 'sidebar.environ', icon: <MdEco />, path: '/Environ' },
  { labelKey: 'sidebar.monitoring', icon: <FiBarChart2 />, path: '/monitoring' },
  { labelKey: 'sidebar.adc', icon: <FiShield />, path: '/adc' },
  { labelKey: 'sidebar.logs', icon: <FiTable />, path: '/logs' },
  { labelKey: 'sidebar.report', icon: <FiFileText />, path: '/report' },
  { labelKey: 'sidebar.aiModel', icon: <FiCpu />, path: '/ai-model-beta' },
  { labelKey: 'sidebar.settings', icon: <FiSettings />, path: '/settings' },
];

const getAdcDisabledStatus = () => {
  const connectionType = sessionStorage.getItem('connectionType');
  return connectionType === 'WiFi' || connectionType === 'TCP/IP MODBUS';
};

const Sidebar = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isAdcDisabled, setIsAdcDisabled] = useState(getAdcDisabledStatus());

  useEffect(() => {
    const handleStorageUpdate = () => {
      setIsAdcDisabled(getAdcDisabledStatus());
    };
    window.addEventListener('storageUpdated', handleStorageUpdate);
    return () => {
      window.removeEventListener('storageUpdated', handleStorageUpdate);
    };
  }, []);

  return (
    <>
      {!mobileOpen && (
        <button
          className="sm:hidden fixed top-4 left-4 z-50 p-2.5 rounded-xl bg-[#1B2355] text-white shadow-lg"
          onClick={() => setMobileOpen(true)}
        >
          <FiMenu size={22} />
        </button>
      )}

      <aside
        className={`
          transition-all duration-300 ease-in-out h-full fixed sm:static top-0 left-0 z-[2000]
          ${collapsed ? "w-16 xl:w-20" : "w-56 xl:w-64"}
          ${mobileOpen ? "translate-x-0" : "-translate-x-full sm:translate-x-0"}
          text-white shadow-2xl flex flex-col overflow-x-hidden overflow-y-hidden shrink-0
        `}
        style={{ background: "#0c1730" }}
      >
        <div className="flex items-center gap-1 px-4 py-4 border-b border-white/10 min-w-0">
          {!collapsed && (
            <div className="flex-1 min-w-0 overflow-hidden">
              <p className="text-[26px] font-extrabold leading-none tracking-tight text-white">HL Mando</p>
            </div>
          )}
          <button
            type="button"
            className={`text-gray-400 hover:text-white hidden sm:flex items-center justify-center p-1 rounded-md hover:bg-white/10 transition-all duration-300 shrink-0 ${
              collapsed ? "mx-auto" : ""
            }`}
            onClick={() => setCollapsed(!collapsed)}
          >
            <FiMenu size={16} />
          </button>
          <button
            type="button"
            className="text-gray-400 hover:text-white sm:hidden flex items-center justify-center p-1 rounded-md hover:bg-white/10 transition-all duration-300 shrink-0"
            onClick={() => setMobileOpen(false)}
          >
            <FiX size={18} />
          </button>
        </div>

        <nav className={`mt-3 flex-1 overflow-y-auto overflow-x-hidden ${collapsed ? "px-1" : "px-2.5"}`}>
          {navItems.map((item) => {
            const isDisabled = (item.path === '/adc' || item.path === 'adc') && isAdcDisabled;
            const label = t(item.labelKey);

            return (
              <NavLink
                to={item.path}
                key={item.path}
                end={item.path === '/dashboard'}
                title={collapsed ? label : undefined}
                onClick={(e) => {
                  e.preventDefault();
                  if (isDisabled) return;
                  setMobileOpen(false);
                  flushSync(() => {
                    navigate(item.path);
                  });
                }}
                className={({ isActive }) =>
                  `group relative flex items-center py-2.5 mb-1 cursor-pointer transition-all duration-300 rounded-xl ${
                    collapsed ? "justify-center px-0 gap-0" : "gap-3 px-3"
                  } ${
                    isDisabled
                      ? "text-slate-600 cursor-not-allowed"
                      : isActive
                      ? "text-white shadow-md"
                      : "text-slate-200 hover:bg-white/5 hover:text-white"
                  }`
                }
                style={({ isActive }) =>
                  isActive && !isDisabled
                    ? { background: "linear-gradient(90deg, #1d4f86 0%, #163a66 100%)" }
                    : undefined
                }
                aria-disabled={isDisabled}
                tabIndex={isDisabled ? -1 : 0}
              >
                <span className="text-[16px] shrink-0 opacity-90">{item.icon}</span>
                {!collapsed && (
                  <>
                    <span className="text-[13px] font-medium tracking-wide truncate flex-1">{label}</span>
                    <FiChevronRight className="shrink-0 opacity-70" size={14} />
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className={`${collapsed ? "px-1" : "px-4"} pb-5 mt-auto relative`}>
          <button
            type="button"
            title={t("login.signOut")}
            onClick={() => {
              logout();
              setMobileOpen(false);
              navigate("/");
            }}
            className={`w-full flex items-center rounded-lg text-slate-300 hover:bg-white/10 hover:text-white transition-all mb-3 ${
              collapsed ? "justify-center py-2.5" : "gap-2.5 px-1 py-2"
            }`}
          >
            <FiLogOut size={16} className="shrink-0" />
            {!collapsed && (
              <span className="min-w-0 text-left">
                <span className="block text-[12px] font-medium">{t("login.signOut")}</span>
                {getAuthDisplayName() ? (
                  <span className="block text-[10px] text-slate-400 truncate">{getAuthDisplayName()}</span>
                ) : null}
              </span>
            )}
          </button>
          {!collapsed ? (
            <div className="relative">
              <p className="text-[22px] font-extrabold leading-none tracking-tight">
                <span className="text-[#3ec6ff]">e<span className="relative -top-[0.18em] inline-block">-</span></span>HAECHIE
              </p>
              <p className="mt-2 text-[12px] leading-snug text-slate-300">
                Electronic Power<br />Arc Detection &amp; Safety<br />Monitoring
              </p>
              <div
                className="pointer-events-none absolute -bottom-6 -left-6 h-16 w-16 rounded-full"
                style={{ background: "radial-gradient(circle, rgba(62,198,255,0.35) 0%, rgba(62,198,255,0) 70%)" }}
              />
            </div>
          ) : (
            <div className="flex justify-center py-1 text-[11px] font-extrabold text-[#3ec6ff]">e</div>
          )}
        </div>
      </aside>

      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 sm:hidden z-30 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
        />
      )}
    </>
  );
};

export default Sidebar;
